import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"
import {
  STATUS_OUTCOMES, clearUnavailable, hasConflictingIdentity, isStatusObject,
  showUnavailable, statusTimeElement, validateStatus
} from "../lib/upload_status_feedback"
import { applyStatusFragment, parseStatusFragment } from "../lib/upload_status_fragment"

const POLL_DELAYS = [2000, 4000, 8000, 15000]
const TERMINAL_STATES = new Set([
  "ready",
  "rejected",
  "failed",
  "cancelled",
  "superseded",
  "expired"
])

export default class extends Controller {
  static values = { batchUrl: String }

  connect() {
    this.pollDelayIndex = 0
    this.pollTimer = null
    this.pollInFlight = false
    this.pushConnected = false
    this.refreshPending = false
    this.notifiedVersions = new Map()
    this.connectedIdentifiers = new Set()
    this.subscriptions = []
    this.receiveEvent = (event) => this.receivePush(event.detail)
    this.visibilityEvent = () => this.visibilityChanged()
    this.onlineEvent = () => this.onlineChanged()

    this.element.addEventListener("cohort-upload-status:received", this.receiveEvent)
    document.addEventListener("visibilitychange", this.visibilityEvent)
    window.addEventListener("online", this.onlineEvent)
    window.addEventListener("offline", this.onlineEvent)

    this.subscribe()
    this.poll({ scheduleNext: true })
  }

  disconnect() {
    this.cancelPoll()
    this.subscriptions.forEach((subscription) => subscription.unsubscribe())
    this.subscriptions = []
    this.element.removeEventListener("cohort-upload-status:received", this.receiveEvent)
    document.removeEventListener("visibilitychange", this.visibilityEvent)
    window.removeEventListener("online", this.onlineEvent)
    window.removeEventListener("offline", this.onlineEvent)
  }

  subscribe() {
    this.activeItems().forEach((item) => {
      const identifier = this.itemIdentifier(item)
      const subscription = consumer.subscriptions.create(
        {
          channel: "CohortUploadStatusChannel",
          upload_id: item.dataset.uploadId,
          purpose: item.dataset.uploadPurpose
        },
        {
          connected: () => this.pushRecovered(identifier),
          disconnected: () => this.pushLost(identifier),
          rejected: () => this.pushLost(identifier),
          received: (payload) => this.receivePush(payload, item)
        }
      )
      this.subscriptions.push(subscription)
    })
  }

  pushRecovered(identifier) {
    const wasDisconnected = !this.pushConnected
    this.connectedIdentifiers.add(identifier)
    this.pushConnected = this.activeItems().every(
      (item) => this.connectedIdentifiers.has(this.itemIdentifier(item))
    )
    if (!this.pushConnected) return

    this.cancelPoll()
    if (wasDisconnected) {
      this.pollDelayIndex = 0
      this.poll({ scheduleNext: false })
    }
  }

  pushLost(identifier) {
    this.connectedIdentifiers.delete(identifier)
    this.pushConnected = false
    this.schedulePoll()
  }

  receivePush(payload, boundItem = null) {
    if (boundItem && hasConflictingIdentity(payload, boundItem)) return STATUS_OUTCOMES.ignored
    const item = boundItem || (isStatusObject(payload) && this.findItem(payload.upload_id, payload.purpose))
    if (!item || !this.element.contains(item)) return STATUS_OUTCOMES.ignored
    const validated = validateStatus(this.element, item, payload)
    if (!validated) {
      const outcome = this.markUnavailable(item)
      this.pushConnected = false
      this.schedulePoll()
      return outcome
    }
    const { incomingTime, currentTime } = validated
    if (incomingTime < currentTime ||
        (incomingTime === currentTime && item.dataset.uploadState !== payload.state)) return STATUS_OUTCOMES.ignored
    if (incomingTime === currentTime) {
      clearUnavailable(item)
      return STATUS_OUTCOMES.unchanged
    }
    if (incomingTime <= (this.notifiedVersions.get(item) || 0)) return STATUS_OUTCOMES.ignored

    this.notifiedVersions.set(item, incomingTime)
    // Cable has no request/session context for CSRF form fields. Coalesce its
    // notifications into an authorized refresh, including a notification that
    // arrives after an already-running poll took its database snapshot.
    if (this.pollInFlight) this.refreshPending = true
    else this.poll({ scheduleNext: false })
    return STATUS_OUTCOMES.refreshing
  }

  receive(payload, { boundItem = null, requestedVersions = null } = {}) {
    if (boundItem && hasConflictingIdentity(payload, boundItem)) return STATUS_OUTCOMES.ignored
    const item = boundItem || (isStatusObject(payload) && this.findItem(payload.upload_id, payload.purpose))
    if (!item || !this.element.contains(item)) return STATUS_OUTCOMES.ignored
    if (requestedVersions && !requestedVersions.has(item)) return STATUS_OUTCOMES.ignored

    const validated = validateStatus(this.element, item, payload)
    if (!validated) return this.markUnavailable(item, requestedVersions?.get(item))
    const { incomingTime, currentTime } = validated
    if (incomingTime < currentTime) return STATUS_OUTCOMES.ignored
    if (incomingTime < (this.notifiedVersions.get(item) || 0)) {
      this.pushConnected = false
      return STATUS_OUTCOMES.ignored
    }
    const fragment = parseStatusFragment(item, payload)
    if (!fragment) return this.markUnavailable(item, requestedVersions?.get(item))
    if (incomingTime === currentTime) {
      if (item.dataset.uploadState !== payload.state) return STATUS_OUTCOMES.ignored
      // A normal unchanged poll can restore availability without changing this
      // consumer's existing rule that all equal-time state updates are ignored.
      clearUnavailable(item)
      return STATUS_OUTCOMES.unchanged
    }

    this.notifiedVersions.delete(item)
    clearUnavailable(item)
    return this.updateItem(item, payload, fragment)
  }

  updateItem(item, payload, fragment) {
    applyStatusFragment(item, fragment)
    item.dataset.uploadState = payload.state
    if (typeof payload.published === "boolean") {
      item.dataset.uploadPublished = String(payload.published)
    }
    if (TERMINAL_STATES.has(payload.state)) {
      this.removeSubscription(item.dataset.uploadId, item.dataset.uploadPurpose)
    }
    if (this.activeItems().length === 0) this.cancelPoll()
    return STATUS_OUTCOMES.applied
  }

  async poll({ scheduleNext }) {
    const items = this.activeItems()
    if (!this.canPoll() || this.pollInFlight || items.length === 0) return
    const requestedVersions = new Map(items.map((item) => [item, {
      state: item.dataset.uploadState,
      time: statusTimeElement(item)?.dateTime
    }]))

    this.pollInFlight = true
    try {
      const response = await fetch(this.batchUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-Upload-Fragment": "true",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
        },
        body: JSON.stringify({
          uploads: items.map((item) => ({
            upload_id: item.dataset.uploadId,
            purpose: item.dataset.uploadPurpose
          }))
        })
      })
      if (!response.ok) throw new Error("Upload status request failed")

      const payload = await response.json()
      if (!isStatusObject(payload) || !Array.isArray(payload.uploads)) throw new Error("Invalid upload status response")
      const outcomes = payload.uploads.map((status) => this.receive(status, { requestedVersions }))
      if (outcomes.includes(STATUS_OUTCOMES.unavailable)) this.pushConnected = false
    } catch (_error) {
      const outcomes = items.map((item) => this.markUnavailable(item, requestedVersions.get(item)))
      if (outcomes.includes(STATUS_OUTCOMES.unavailable)) this.pushConnected = false
    } finally {
      this.pollInFlight = false
      if (this.refreshPending) {
        this.refreshPending = false
        this.poll({ scheduleNext })
      } else if (!this.pushConnected && (scheduleNext || !this.pollTimer)) this.schedulePoll()
    }
  }

  markUnavailable(item, requestedVersion = null) {
    if (!this.activeItems().includes(item)) return STATUS_OUTCOMES.ignored
    if (requestedVersion && (item.dataset.uploadState !== requestedVersion.state ||
        statusTimeElement(item)?.dateTime !== requestedVersion.time)) return STATUS_OUTCOMES.ignored

    return showUnavailable(item)
  }

  schedulePoll() {
    if (!this.canPoll() || this.pollTimer || this.pollInFlight ||
        this.activeItems().length === 0) return

    const delay = POLL_DELAYS[Math.min(this.pollDelayIndex, POLL_DELAYS.length - 1)]
    this.pollDelayIndex = Math.min(this.pollDelayIndex + 1, POLL_DELAYS.length - 1)
    this.pollTimer = window.setTimeout(() => {
      this.pollTimer = null
      this.poll({ scheduleNext: true })
    }, delay)
  }

  cancelPoll() {
    if (this.pollTimer) window.clearTimeout(this.pollTimer)
    this.pollTimer = null
  }

  visibilityChanged() {
    if (document.hidden) {
      this.cancelPoll()
    } else if (!this.pushConnected) {
      this.poll({ scheduleNext: true })
    }
  }

  onlineChanged() {
    if (!navigator.onLine) {
      this.cancelPoll()
    } else if (!this.pushConnected) {
      this.poll({ scheduleNext: true })
    }
  }

  canPoll() {
    return !document.hidden && navigator.onLine !== false
  }

  activeItems() {
    return Array.from(this.element.querySelectorAll("[data-upload-item]")).filter(
      (item) => !TERMINAL_STATES.has(item.dataset.uploadState)
    )
  }

  findItem(uploadId, purpose) {
    return Array.from(this.element.querySelectorAll("[data-upload-item]")).find(
      (item) => item.dataset.uploadId === uploadId && item.dataset.uploadPurpose === purpose
    )
  }

  removeSubscription(uploadId, purpose) {
    const removedIdentifier = this.identifier(uploadId, purpose)
    const remaining = []
    this.subscriptions.forEach((subscription) => {
      const identifier = JSON.parse(subscription.identifier)
      if (identifier.upload_id === uploadId && identifier.purpose === purpose) {
        subscription.unsubscribe()
      } else {
        remaining.push(subscription)
      }
    })
    this.subscriptions = remaining
    this.connectedIdentifiers.delete(removedIdentifier)
    this.pushConnected = this.activeItems().every(
      (item) => this.connectedIdentifiers.has(this.itemIdentifier(item))
    )
  }

  itemIdentifier(item) {
    return this.identifier(item.dataset.uploadId, item.dataset.uploadPurpose)
  }

  identifier(uploadId, purpose) {
    return `${uploadId}:${purpose}`
  }
}
